import React, { useEffect, useState } from 'react';
import PollDatesDialog from './PollDatesDialog';

// (26 Oct 2026, decision 145) The Event Administrator's poll controls, on their own
// event's card on the member screen (MemberCalendarStep.jsx). An EA is otherwise a plain
// member, so they have no dashboard: these are the administrator's poll buttons for this
// one event, with the same confirmation and the same "Email N members / don't email"
// choice (the standing rule since 26 Sep, H3):
//   - View results (every answer, the roster and "Answer for them");
//   - Close Poll Phase 1 / Phase 2 Now, while a phase is open;
//   - before any poll exists: "Poll phase 1 (offers) opens {date} ›" or "No poll will
//     open by itself ›" (the Poll dates window, decision 131) and Open Phase 1 (offers).
// Changes to the event itself stay with the Group Administrator. The server checks the
// EA's rights again on every action (pollRoutes.js, calendarRoutes.js).
// Kept separate from DashboardStep.jsx's own copy of these controls so the
// administrator's dashboard is untouched; the two must say the same things.

// A YYYY-MM-DD date in full, e.g. "Sunday 8 November", read as it is.
const fullDayDate = (isoDate) => {
  const iso = String(isoDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
};
// Today in France, as YYYY-MM-DD: a poll can only be opened for an event after today.
const todayInFrance = () => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date()).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const pillClass = 'text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap transition';

export default function EventAdminPollControls({ evt, calendarId, apiBaseUrl, authToken, onStatusMessage, onChanged, onViewResults }) {
  const [confirm, setConfirm] = useState(null); // { type: 'open' | 'close', poll? }
  const [emailCount, setEmailCount] = useState(undefined); // undefined = working it out, null = unknown
  const [busy, setBusy] = useState(false);
  const [datesQuestion, setDatesQuestion] = useState(null); // { mode, timetable }

  const headers = { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' };
  const status = (msg, type = 'success', duration) => { if (onStatusMessage) onStatusMessage(msg, type, duration); };

  const poll = evt.poll || null;
  const pollOpen = !!(poll && poll.status === 'open');
  const canOpen = !poll && !evt.hasPollHistory && String(evt.eventDate || '').slice(0, 10) > todayInFrance();

  // How many members the action would email, after their own email settings.
  useEffect(() => {
    if (!confirm) { setEmailCount(undefined); return undefined; }
    const reason = confirm.type === 'open' ? 'phase1_open' : (confirm.poll.phase === 'offer' ? 'phase2_open' : 'phase2_closed');
    let cancelled = false;
    setEmailCount(undefined);
    fetch(`${apiBaseUrl}/api/events/${evt.eventUid}/polls/member-email-count?reason=${reason}`, { headers })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (!cancelled) setEmailCount(ok && typeof d.count === 'number' ? d.count : null); })
      .catch(() => { if (!cancelled) setEmailCount(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirm, evt.eventUid, apiBaseUrl]);

  const runPollAction = async (notify) => {
    if (!confirm) return;
    const { type } = confirm;
    setBusy(true);
    try {
      const url = type === 'open'
        ? `${apiBaseUrl}/api/events/${evt.eventUid}/polls/open`
        : `${apiBaseUrl}/api/polls/${confirm.poll.id}/close`;
      const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify({ notify: notify === true }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        status(data.error || `Failed to ${type === 'open' ? 'open' : 'close'} the poll`, 'error');
        return;
      }
      // Same messages as the administrator's dashboard (DashboardStep.jsx executePollAction).
      let msg = 'Poll closed.';
      const zeroOffers = type === 'close' && confirm.poll.phase === 'offer' && !data.confirmPoll;
      if (type === 'open') msg = 'Phase 1 poll opened.';
      else if (confirm.poll.phase === 'offer') {
        msg = data.confirmPoll
          ? 'Phase 1 closed — Phase 2 (confirm) is now open.'
          : 'Phase 1 closed with no course offers — Phase 2 was not started. You may want to check in with the group directly.';
      } else msg = 'Phase 2 closed — poll cycle complete.';
      msg += (notify === true && !zeroOffers) ? ' Members are being emailed.' : ' No member emails sent.';
      status(msg, zeroOffers ? 'info' : 'success', 6000);
      setConfirm(null);
      if (onChanged) await onChanged();
    } catch (err) {
      status(`Failed to ${type === 'open' ? 'open' : 'close'} the poll`, 'error');
    } finally {
      setBusy(false);
    }
  };

  // The Poll dates window (calendarRoutes.js POST .../poll-dates), as App.js drives it
  // for the administrator: open asks for the timetable, an answer is sent the same way.
  const datesUrl = `${apiBaseUrl}/api/calendars/${calendarId}/events/${evt.eventUid}/poll-dates`;
  const openPollDates = async () => {
    try {
      const res = await fetch(datesUrl, { method: 'POST', headers, body: JSON.stringify({}) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.timetable) {
        status(data.error || 'Could not load the poll dates', 'error');
        if (onChanged) onChanged();
        return;
      }
      setDatesQuestion({ mode: data.mode, timetable: data.timetable });
    } catch (err) {
      status('Could not load the poll dates', 'error');
    }
  };
  const answerPollDates = async (answer) => {
    const plan = answer && answer.pollPlan;
    if (!datesQuestion || !plan) return;
    if (plan.action === 'keep' && datesQuestion.mode === 'stay') {
      setDatesQuestion(null);
      status('The poll dates are unchanged.');
      return;
    }
    try {
      const res = await fetch(datesUrl, { method: 'POST', headers, body: JSON.stringify({ pollPlan: plan }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        status(data.error || 'Could not save the poll dates', 'error');
        return;
      }
      setDatesQuestion(null);
      status(data.note || 'Poll dates saved.', 'success', 'long');
      if (onChanged) await onChanged();
    } catch (err) {
      status('Could not save the poll dates', 'error');
    }
  };

  const verb = confirm && confirm.type === 'open' ? 'Open' : 'Close';
  const emailLabel = emailCount === undefined || emailCount === null
    ? `${verb} and email members`
    : `${verb} and email ${emailCount} member${emailCount === 1 ? '' : 's'}`;

  return (
    <>
      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-xs font-semibold text-gray-600 mb-2">You are this event's administrator</p>
        <div className="flex flex-wrap items-center gap-2">
          {pollOpen && (
            <button
              type="button"
              onClick={() => setConfirm({ type: 'close', poll })}
              disabled={busy}
              className={`${pillClass} border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50`}
            >
              {poll.phase === 'offer' ? 'Close Poll Phase 1 Now' : 'Close Poll Phase 2 Now'}
            </button>
          )}
          {canOpen && evt.pollOpensOn && (
            <button type="button" onClick={openPollDates} className={`${pillClass} bg-gray-100 text-gray-600 hover:bg-gray-200`}>
              Poll phase 1 (offers) opens {fullDayDate(evt.pollOpensOn)} {'›'}
            </button>
          )}
          {canOpen && !evt.pollOpensOn && evt.pollSuppressed && (
            <button type="button" onClick={openPollDates} className={`${pillClass} bg-gray-100 text-gray-600 hover:bg-gray-200`}>
              No poll will open by itself {'›'}
            </button>
          )}
          {canOpen && (
            <button
              type="button"
              onClick={() => setConfirm({ type: 'open' })}
              disabled={busy}
              className={`${pillClass} border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50`}
            >
              Open Phase 1 (offers)
            </button>
          )}
          {poll && onViewResults && (
            <button
              type="button"
              onClick={() => onViewResults(evt.eventUid)}
              className={`${pillClass} border border-blue-300 text-blue-600 hover:bg-blue-50`}
            >
              View Results
            </button>
          )}
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {confirm.type === 'open'
                ? 'Open Phase 1 poll now?'
                : (confirm.poll.phase === 'offer' ? 'Close Poll Phase 1 now?' : 'Close Poll Phase 2 now?')}
            </h3>
            <p className="text-gray-600 mb-4">
              {confirm.type === 'open'
                ? `This opens the offer-phase poll for "${evt.title}" immediately, regardless of the calendar's usual timing settings.`
                : (confirm.poll.phase === 'offer'
                  ? `This closes phase 1 (the offer-phase) of the poll for the "${evt.title}" event immediately and advances to Phase 2 (or flags zero offers, if none were made). This cannot be undone.`
                  : `This closes phase 2 (the confirm-phase) of the poll for the "${evt.title}" event immediately. This cannot be undone.`)}
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-gray-700">
              {emailCount === undefined
                ? 'Working out how many members would be emailed…'
                : emailCount === null
                  ? 'Members would be emailed according to their own email settings (the number could not be worked out).'
                  : emailCount === 0
                    ? 'No members would be emailed (their email settings exclude this email).'
                    : `${emailCount} member${emailCount === 1 ? '' : 's'} would be emailed, according to their own email settings.`}
              {confirm.type === 'close' && confirm.poll.phase === 'offer' && (
                <span className="block mt-1 text-gray-500">If no dishes were offered, Phase 2 won't open and no one is emailed.</span>
              )}
            </div>
            <div className="flex flex-wrap gap-3 justify-end">
              <button
                onClick={() => setConfirm(null)}
                disabled={busy}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => runPollAction(false)}
                disabled={busy}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-800 hover:bg-gray-50 disabled:opacity-50"
              >
                {busy ? 'Working…' : `${verb}, don't email`}
              </button>
              {emailCount !== 0 && (
                <button
                  onClick={() => runPollAction(true)}
                  disabled={busy || emailCount === undefined}
                  className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 ${confirm.type === 'open' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  {busy ? 'Working…' : emailLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {datesQuestion && (
        <PollDatesDialog
          timetable={datesQuestion.timetable}
          eventTitle={evt.title}
          mode={datesQuestion.mode}
          onAnswer={answerPollDates}
          onCancel={() => setDatesQuestion(null)}
        />
      )}
    </>
  );
}
